const typeScale = [
  {
    label: "Display",
    token: "56/64",
    sample: "Their skills, your health.",
    className: "font-serif text-5xl font-bold tracking-tight",
  },
  {
    label: "H1",
    token: "44/52",
    sample: "Health intelligence platform",
    className: "font-serif text-4xl font-bold tracking-tight",
  },
  {
    label: "H2",
    token: "34/42",
    sample: "Guided by clarity",
    className: "font-serif text-3xl font-bold tracking-tight",
  },
  {
    label: "H3",
    token: "26/34",
    sample: "Personalized insight",
    className: "font-serif text-2xl font-semibold tracking-tight",
  },
  {
    label: "Body Large",
    token: "20/30",
    sample: "A confident narrative for key content and brand messaging.",
    className: "text-xl font-medium",
  },
  {
    label: "Body",
    token: "16/26",
    sample: "Default reading style for interface and long-form copy.",
    className: "text-base",
  },
  {
    label: "Small",
    token: "14/22",
    sample: "Supporting metadata and explanations.",
    className: "text-sm",
  },
  {
    label: "Label",
    token: "11/16",
    sample: "SECTION LABEL",
    className: "text-[11px] font-semibold tracking-[0.2em] uppercase",
  },
];

const weights = [
  { label: "Light", value: 300 },
  { label: "Regular", value: 400 },
  { label: "Medium", value: 500 },
  { label: "Semibold", value: 600 },
  { label: "Bold", value: 700 },
];

export default function Typography() {
  return (
    <section
      id="typography"
      className="kin-section-glow border-t border-kin-olive/10 px-6 py-14 scroll-mt-16 sm:px-8 sm:py-16 gradient-wash"
    >
      <div className="mx-auto max-w-6xl">
        <p className="text-sm font-semibold tracking-[0.22em] uppercase text-kin-muted">
          03 - Typography
        </p>

        <div className="mt-6 max-w-3xl">
          <h2 className="font-serif text-4xl font-bold text-kin-black sm:text-5xl">
            Two voices, one cadence
          </h2>
          <p className="mt-4 text-base leading-relaxed text-kin-body/90 sm:text-lg">
            Cormorant Garamond carries emotion and authority. Manrope handles product clarity.
            Together they create a distinctive yet functional reading experience.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <article className="kin-surface-strong rounded-[24px] p-6 sm:p-7">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-green-dark">
              Serif
            </p>
            <p className="mt-4 font-serif text-5xl font-bold leading-tight text-kin-black">
              Cormorant Garamond
            </p>
            <p className="mt-4 font-serif text-xl italic text-kin-black-light">
              The quick brown fox jumps over the lazy dog
            </p>
            <p className="mt-5 font-serif text-lg text-kin-black">
              Aa Bb Cc Dd Ee Ff Gg Hh Ii Jj Kk Ll Mm
            </p>
            <p className="font-serif text-lg text-kin-black">
              Nn Oo Pp Qq Rr Ss Tt Uu Vv Ww Xx Yy Zz
            </p>
            <p className="mt-2 font-serif text-lg text-kin-black">0 1 2 3 4 5 6 7 8 9</p>
            <p className="mt-5 text-sm leading-relaxed text-kin-body/85">
              Use for hero headlines, section titles, and brand-signature messaging.
            </p>
          </article>

          <article className="kin-surface rounded-[24px] p-6 sm:p-7">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-green-dark">
              Sans
            </p>
            <p className="mt-4 text-4xl font-light text-kin-black">Manrope</p>
            <p className="mt-4 text-lg text-kin-black-light">
              The quick brown fox jumps over the lazy dog
            </p>
            <p className="mt-5 text-lg text-kin-black">Aa Bb Cc Dd Ee Ff Gg Hh Ii Jj Kk Ll Mm</p>
            <p className="text-lg text-kin-black">Nn Oo Pp Qq Rr Ss Tt Uu Vv Ww Xx Yy Zz</p>
            <p className="mt-2 text-lg text-kin-black">0 1 2 3 4 5 6 7 8 9</p>

            <div className="mt-5 flex flex-wrap gap-2">
              {weights.map((weight) => (
                <span
                  key={weight.label}
                  className="rounded-full border border-kin-white/50 bg-kin-white/60 shadow-sm backdrop-blur-md px-3 py-1 text-xs text-kin-body"
                  style={{ fontWeight: weight.value }}
                >
                  {weight.label}
                </span>
              ))}
            </div>

            <p className="mt-5 text-sm leading-relaxed text-kin-body/85">
              Use for body copy, labels, controls, navigation, data values, and operational UI.
            </p>
          </article>
        </div>

        <article className="mt-10 kin-surface rounded-[24px] p-6 sm:p-8">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
            Type Scale
          </p>
          <div className="mt-4 divide-y divide-kin-olive/12">
            {typeScale.map((level) => (
              <div key={level.label} className="py-4 sm:py-5 sm:flex sm:items-baseline sm:gap-6">
                <div className="mb-2 flex items-center gap-3 sm:mb-0 sm:w-56 sm:shrink-0">
                  <span className="text-xs font-semibold tracking-[0.16em] uppercase text-kin-muted">
                    {level.label}
                  </span>
                  <span className="rounded-full border border-kin-white/50 bg-kin-white/60 shadow-sm backdrop-blur-md px-2 py-0.5 font-mono text-[10px] text-kin-muted">
                    {level.token}
                  </span>
                </div>
                <p className={`${level.className} text-kin-black`}>{level.sample}</p>
              </div>
            ))}
          </div>
        </article>

        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <article className="kin-surface rounded-[24px] p-6">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">Do</p>
            <ul className="mt-3 space-y-2 text-sm text-kin-body/90">
              <li>Use serif for narrative hierarchy and emotional emphasis.</li>
              <li>Keep body copy in Manrope with generous line-height.</li>
              <li>Maintain consistent label spacing and uppercase cadence.</li>
            </ul>
          </article>

          <article className="kin-surface rounded-[24px] p-6">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">Avoid</p>
            <ul className="mt-3 space-y-2 text-sm text-kin-body/90">
              <li>Do not mix more than two font families in product surfaces.</li>
              <li>Avoid long paragraphs in serif at small sizes.</li>
              <li>Do not use all-caps body text outside labels and metadata.</li>
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
}
